import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF, ptBR } from "@mui/x-data-grid";
import { brl, formatIsoDateBR, plural } from "~/utils/format";
import { getRowLock, STATUS_SHORT_LABEL } from "~/utils/mergeSelection";
import OriginTags from "./OriginTags";
import InactiveChip from "~/shared/ui/InactiveChip";

// Coluna de seleção sem "marcar todos" (a trava é por âncora, marcar todos juntaria pacotes incompatíveis).
// Sem o `type`: a 5.17 avisa "column type checkboxSelection not supported" quando ele vem em `columns`; nada mais lê esse type.
const { type: _checkboxType, ...CHECKBOX_COLUMN } = GRID_CHECKBOX_SELECTION_COL_DEF;

export default function PackageGroupSection({ section, selectedIds, anchor, collapsed, onToggleCollapse, onSelectionChange, onPick, onOpen, selectable = true, inactiveCompanyIds = new Set() }) {
  const locks = new Map(section.rows.map((row) => [row.id, getRowLock(row, anchor)]));
  const isSelected = (id) => selectedIds.includes(id);
  const sectionSelected = section.rows.filter((r) => isSelected(r.id)).map((r) => r.id);

  const columns = [
    { ...CHECKBOX_COLUMN, renderHeader: () => null },
    {
      field: "id", headerName: "Pacote", width: 90, sortable: false,
      renderCell: ({ row }) => <Button size="small" onClick={() => onOpen(row.id)}>#{row.id}</Button>,
    },
    { field: "company", headerName: "Empresa", width: 210, sortable: false, valueGetter: ({ row }) => row.company.name },
    {
      field: "status", headerName: "Status", width: 190, sortable: false,
      renderCell: ({ row }) => (
        <Chip size="small" label={row.status === "merged" ? `Agrupado no #${row.mergedIntoAdvancePaymentId}` : STATUS_SHORT_LABEL[row.status] || row.status} />
      ),
    },
    {
      // Motivo logo depois do Status (o aviso "Seleção travada" promete o motivo na linha): na última coluna ficava fora da tela.
      field: "lock", headerName: "Aviso", width: 240, sortable: false,
      renderCell: ({ row }) => {
        const lock = locks.get(row.id);
        if (lock && !isSelected(row.id)) return <span className={`lock-text lock-${lock.kind}`}>{lock.text}</span>;
        return row.hasNonValidated ? <span className="lock-text lock-warn">⚠ valor não validado</span> : "";
      },
    },
    { field: "origin", headerName: "Origem", width: 280, sortable: false, renderCell: ({ row }) => <OriginTags row={row} /> },
    { field: "requestDate", headerName: "Pedido em", width: 110, sortable: false, valueFormatter: ({ value }) => formatIsoDateBR(value) },
    { field: "paymentDate", headerName: "Pagamento do pacote", width: 170, sortable: false, valueFormatter: ({ value }) => (value ? formatIsoDateBR(value) : "a definir") },
    { field: "vacanciesQty", headerName: "Plantões", width: 90, sortable: false, align: "right", headerAlign: "right" },
    { field: "grossTotal", headerName: "Valor bruto", width: 130, sortable: false, align: "right", headerAlign: "right", valueFormatter: ({ value }) => brl(value) },
    {
      field: "netTotal", headerName: "Valor a pagar", width: 150, sortable: false, align: "right", headerAlign: "right",
      // O Antecipe devolve netTotal numérico sempre (COALESCE → 0), mas o líquido só é calculado depois que a data de pagamento existe.
      valueGetter: ({ row }) => (row.paymentDate && row.netTotal != null ? brl(row.netTotal) : "após conferência"),
    },
    { field: "hospitalNames", headerName: "Unidade", width: 220, sortable: false, valueGetter: ({ row }) => (row.hospitalNames || []).join(", ") },
  ];

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1.5, bgcolor: "background.paper" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.50" }}>
        <Box
          component="button"
          type="button"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, border: 0, bgcolor: "transparent", cursor: "pointer", textAlign: "left", p: 0, font: "inherit" }}
        >
          <span aria-hidden="true">{collapsed ? "▶" : "▼"}</span>
          <b>{section.company.name}</b>
          {/* Inativa pela lista de empresas (Leitura+) ou pelo company.active que o merge-groups já devolve em cada grupo. */}
          {(section.company.active === false || inactiveCompanyIds.has(section.company.id)) && (
            <InactiveChip />
          )}
          <span>{section.doctor.name}</span>
          <Typography variant="caption" color="text.secondary">
            CRM {section.doctor.crm}/{section.doctor.crmUf} · {plural(section.rows.length, "pacote", "pacotes")}
          </Typography>
        </Box>
        {section.suggestion ? (
          <>
            <Typography variant="body2" color="secondary">⧉ {section.suggestion.ids.length} pacotes podem ser juntados</Typography>
            {selectable && (
              <Button size="small" variant="outlined" onClick={() => onPick(section.suggestion.ids)}>Selecionar os {section.suggestion.ids.length}</Button>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {section.joinableCount > 1 ? "Nada para juntar: status ou data de pagamento diferentes" : "Nada para juntar"}
          </Typography>
        )}
      </Box>
      {!collapsed && (
        <DataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          rows={section.rows}
          columns={columns}
          autoHeight
          density="compact"
          // Altura automática: a Origem pode ter 2–3 etiquetas empilhadas e a altura fixa da linha as cortava.
          getRowHeight={() => "auto"}
          hideFooter
          disableVirtualization
          disableColumnMenu
          disableColumnFilter
          disableSelectionOnClick
          checkboxSelection={selectable}
          isRowSelectable={({ id }) => isSelected(id) || !locks.get(id)}
          selectionModel={sectionSelected}
          onSelectionModelChange={(model) => onSelectionChange(section.key, model)}
          getRowClassName={({ row }) => (row.status === "merged" ? "row-merged" : locks.get(row.id) && !isSelected(row.id) ? "row-off" : "")}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell": { py: 0.5 },
            "& .row-merged": { color: "text.disabled" },
            "& .row-off": { opacity: 0.7 },
            "& .lock-text": { whiteSpace: "normal", lineHeight: 1.3 },
            "& .lock-off, & .lock-blocked": { color: "#b71c1c", fontSize: 12 },
            "& .lock-merged": { color: "#9e9e9e", fontSize: 12 },
            "& .lock-warn": { color: "#b26a00", fontSize: 12 },
          }}
        />
      )}
    </Box>
  );
}
